import {Project} from '../../entity/model/project';
import moment from 'moment/moment';
import {DropdownOption} from '../../entity/core/dropdown-option';
import {ProjectDto} from '../../entity/dto/project-dto';
import {ManageMode} from '../../entity/enums/manage-mode';
import {RequestBody} from '../../entity/core/request-body';
import {toast} from 'react-toastify';
import {AssignDto} from '../../entity/dto/assign-dto';
import {ChartTitle} from '../../entity/enums/chart-title';
import {ChartStats} from '../../entity/resource/chart-stats';
import {ChartStatus} from '../../entity/resource/chart-status';
import {SearchResponse} from '../../entity/resource/search-response';
import SearchStore from '../store/search-store';
import {SearchDto, SearchOption} from '../../entity/dto/search-dto';
import {SearchEntity} from '../../entity/enums/search-entity';
import {DueDateStats, ProjectStats, StatusStats} from '../../entity/resource/project-stats';
import {ProjectColumn} from '../../entity/enums/project-column';
import {SortType} from '../../entity/enums/sort-type';
import {ProjectInfoDto} from '../../entity/dto/project-info-dto';
import {ParcelInfoDto} from '../../entity/dto/parcel-info-dto';
import {DefaultOrderData} from '../../entity/resource/default-order-data';
import ProjectStore from '../store/project-store';
import {ProjectPermission} from '../../entity/resource/project-permission';
import {TitlePermission} from '../../entity/resource/title-permission';
import {getStatus, OrderStatus} from '../../entity/enums/order-status';

export default class ProjectService {
  projectStore: ProjectStore;
  searchStore: SearchStore;

  constructor(projectStore: ProjectStore, searchStore: SearchStore) {
    this.projectStore = projectStore;
    this.searchStore = searchStore;
  }

  getProjects = async (organizationId: string, isAdminUser: boolean): Promise<Project[]> => {
    const projects = await this.projectStore.getProjects(organizationId, isAdminUser);
        
    this.projectStore.cache = true;
    if (projects) {
      this.projectStore.permissions = [];
      for (const project of projects) {
        const projectPermission: ProjectPermission = {
          projectID: project.id,
          titlePermissions: project.titlePermissions
        };
        this.projectStore.permissions.push(projectPermission);
      }
    }

    return projects;
  }

  searchProjects = async (organizationId: string, page: number, pageSize: number, searchInput?: string, searchOptions?: SearchOption[], sortColumn?: ProjectColumn, sortType?: SortType): Promise<SearchResponse | undefined> => {
    const searchDto: SearchDto = {
      entity: SearchEntity.PROJECT,
      field: searchInput ? ['orderNumber', 'assignedToOrganization', 'assignedToUser', 'searchType', 'county', 'state', 'status', 'address'] : [],
      options: searchOptions ?? [],
      page: page,
      pageSize: pageSize,
      query: searchInput ?? '',
      sortField: sortColumn ? sortColumn : '',
      sortOrder: sortType ? sortType : SortType.ASC
    }

    const requestBody: RequestBody = { body: searchDto };
    const response = await this.searchStore.searchEntity(organizationId, requestBody);
    this.projectStore.cache = true;

    if (typeof response !== 'string') {
      const projects = response.projects;

      if (projects) {
        this.projectStore.permissions = [];
        for (const project of projects) {
          const projectPermission: ProjectPermission = {
            projectID: project.id,
            titlePermissions: project.titlePermissions
          };
          this.projectStore.permissions.push(projectPermission);
        }
      }

      return response;
    }
  }

  getProjectStats = async (organizationId: string): Promise<ProjectStats | undefined> => {
    return this.projectStore.getProjectStats(organizationId);
  }

  getDueChartData = async (dueDateStats?: DueDateStats): Promise<ChartStatus[]> => {
    return [
      {
        status: ChartTitle.PAST_DUE,
        count: dueDateStats?.pastDue ?? 0
      },
      {
        status: ChartTitle.TODAY_DUE,
        count: dueDateStats?.dueToday ?? 0
      },
      {
        status: ChartTitle.DUE_THIS_WEEK,
        count: dueDateStats?.dueThisWeek ?? 0
      },
      {
        status: ChartTitle.DUE_NEXT_WEEK,
        count: dueDateStats?.dueNextWeek ?? 0
      },
      {
        status: ChartTitle.DUE_LATER_THIS_MONTH,
        count: dueDateStats?.dueAfterNextWeek ?? 0
      }
    ];
  }

  getStatusChartData = async (statusStats?: StatusStats): Promise<ChartStats> => {
    const labels = [ChartTitle.OPEN_ORDERS, ChartTitle.PENDING_REVIEW_ORDERS, ChartTitle.PENDING_APPROVAL_ORDERS, ChartTitle.ON_HOLD_ORDERS]
    const data = [statusStats?.OPEN ?? 0, statusStats?.PENDING_REVIEW ?? 0, statusStats?.PENDING_APPROVAL ?? 0, statusStats?.ON_HOLD ?? 0];

    return {
      stats: {
        labels: labels,
        datasets: [
          {
            data: data,
            backgroundColor: ['rgba(20, 186, 109, 1)', 'rgba(255, 196, 110, 1)', 'rgba(206, 49, 198, 1)', 'rgba(204, 0, 0, 1)'],
            borderColor: ['#ffffff', '#ffffff', '#ffffff', '#ffffff'],
            borderWidth: 0
          }
        ]
      },
      status:  [
        {
          status: ChartTitle.OPEN_ORDERS,
          count: statusStats?.OPEN ?? 0
        },
        {
          status: ChartTitle.PENDING_REVIEW_ORDERS,
          count: statusStats?.PENDING_REVIEW ?? 0
        },
        {
          status: ChartTitle.PENDING_APPROVAL_ORDERS,
          count: statusStats?.PENDING_APPROVAL ?? 0
        },
        {
          status: ChartTitle.ON_HOLD_ORDERS,
          count: statusStats?.ON_HOLD ?? 0
        }
      ]
    };
  }

  createProject = async (organizationId: string, payload: ProjectDto): Promise<void> => {
    const requestBody: RequestBody = {body: payload};
    const response = await this.projectStore.createProject(organizationId, requestBody);

    if (!response)
      toast.success('File created successfully !!');
    else
      toast.error(response);
  }

  getProjectInfoDto = (searchTypeOptions: DropdownOption[], mode?: ManageMode, project?: Project): ProjectInfoDto => {
    return {
      orderNumber: mode === ManageMode.CREATE ? '' : project?.orderNumber ?? '',
      dueDate: mode === ManageMode.CREATE ? moment().format('YYYY-MM-DD') : moment.utc(project?.dueDate).format('YYYY-MM-DD'),
      searchType: mode === ManageMode.CREATE ? '' : searchTypeOptions.find((option: DropdownOption) => option.key === project?.searchType)?.value ?? '',
      buyers: mode === ManageMode.CREATE ? '' : project?.buyers ?? '',
      sellers: mode === ManageMode.CREATE ? '' : project?.sellers ?? '',
      client: mode === ManageMode.CREATE ? '' : project?.client ?? '',
      stateID: mode === ManageMode.CREATE ? '' : project?.stateID ?? '',
      countyID: mode === ManageMode.CREATE ? '' : project?.countyID ?? '',
      address: '',
      tmsParcelNumber: '',
      titleSectionTemplateID: '',
      assignedToOrganizationID: mode === ManageMode.CREATE ? '' : project?.assignedToOrganizationID ?? '',
      assignedToUserID: mode === ManageMode.CREATE ? '' : project?.assignedToUserID ?? '',
      note: mode === ManageMode.CREATE ? '' : project?.note ?? '',
      assignedToApproverUserID: '',
      assignedToReviewerUserID: ''
    }
  }

  getParcelInfoDto = (mode?: ManageMode, parcelEntry?: ParcelInfoDto, defaultData?: DefaultOrderData): ParcelInfoDto => {
    return {
      id: parcelEntry?.id ?? crypto.randomUUID(),
      assignedToOrganizationID: mode === ManageMode.CREATE ? defaultData?.assignedToOrganizationID ?? '' : parcelEntry?.assignedToOrganizationID ?? '',
      assignedToApproverUserID: mode === ManageMode.CREATE ? '' : parcelEntry?.assignedToApproverUserID ?? '',
      assignedToUserID: mode === ManageMode.CREATE ? defaultData?.assignedToUserID ?? '' : parcelEntry?.assignedToUserID ?? '',
      assignedToReviewerUserID: mode === ManageMode.CREATE ? '' : parcelEntry?.assignedToReviewerUserID ?? '',
      stateID: mode === ManageMode.CREATE ? defaultData?.stateID ?? '' : parcelEntry?.stateID ?? '',
      countyID: mode === ManageMode.CREATE ? defaultData?.countyID ?? '' : parcelEntry?.countyID ?? '',
      tmsParcelNumber: mode === ManageMode.CREATE ? '' : parcelEntry?.tmsParcelNumber ?? '',
      address: mode === ManageMode.CREATE ? '' : parcelEntry?.address ?? '',
      titleSectionTemplateID: defaultData?.titleSectionTemplateID ?? '',
      tmsParcelNumbers: mode === ManageMode.CREATE ? [] : parcelEntry?.tmsParcelNumbers ?? [],
      status: mode === ManageMode.CREATE ? getStatus(OrderStatus.OPEN) : parcelEntry?.status ?? getStatus(OrderStatus.OPEN),
      createdByUserID: mode === ManageMode.CREATE ? '' : parcelEntry?.createdByUserID ?? ''
    }
  }

  updateProject = async (organizationId: string, projectId: string, payload: ProjectDto): Promise<void> => {
    const requestBody: RequestBody = {body: payload};
    const response = await this.projectStore.updateProject(organizationId, projectId, requestBody);

    if (!response)
      toast.success('File updated successfully !!');
    else
      toast.error(response);
  }

  assignProject = async (organizationId: string, projectId: string, payload: AssignDto[]): Promise<void> => {
    const requestBody: RequestBody = {body: payload};
    const response = await this.projectStore.assignProject(organizationId, projectId, requestBody);

    if (!response)
      toast.success('File assigned successfully !!!');
    else
      toast.error(response);
  }

  getAssignDto = (parcelEntry?: ParcelInfoDto): AssignDto => {
    return {
      projectTitleID: parcelEntry?.id ?? '',
      assignedToUserID: parcelEntry?.assignedToUserID ?? '',
      assignedToReviewerUserID: parcelEntry?.assignedToReviewerUserID ?? ''
    };
  }

  deleteProjects = async (organizationId: string, projectId: string, isLastProject: boolean): Promise<void> => {
    const response = await this.projectStore.deleteProject(organizationId, projectId);

    if (!response)
      if (isLastProject) {
        this.projectStore.cache = false;
        toast.success('File deleted successfully !!');
      }
      else
        toast.error(response);
  }

  filterEntries = (entries: ParcelInfoDto[], input: string): ParcelInfoDto[] => {
    return entries.filter((entry: ParcelInfoDto): boolean => {
      const matchesTMS = entry.tmsParcelNumber?.trim().toLowerCase().startsWith(input);
      const matchesAddress = entry.address.trim().toLowerCase().startsWith(input);
      return matchesTMS || matchesAddress;
    });
  }

  getCache = (): boolean => {
    return this.projectStore.cache;
  }

  setCache = (status: boolean): void => {
    this.projectStore.cache = status;
  }

  getProjectById = async (organizationId: string, projectId: string): Promise<Project | undefined> => {
    const project = await this.projectStore.getProjectById(organizationId, projectId);
    if (project && this.projectStore.permissions.length === 0) {
      const projectPermission: ProjectPermission = {
        projectID: projectId,
        titlePermissions: project.titlePermissions
      };

      this.projectStore.permissions.push(projectPermission);
    }

    return project;
  }

  isPermittedProject = (projectId: string): boolean => {
    const projectPermission = this.projectStore.permissions.find((projectPermission: ProjectPermission): boolean => projectPermission.projectID === projectId);

    return projectPermission?.titlePermissions.some((titlePermission: TitlePermission): boolean => titlePermission.hasAccess) ?? false;
  }

  isPermittedTitle = (titleId: string): boolean => {
    const projectPermission = this.projectStore.permissions.find((projectPermission: ProjectPermission): boolean => projectPermission.titlePermissions.some((titlePermission: TitlePermission) => titlePermission.titleID === titleId));
    if (!projectPermission)
      return true;

    return projectPermission.titlePermissions.find((titlePermission: TitlePermission): boolean => titlePermission.titleID === titleId)?.hasAccess ?? false;
  }

  moveProjectToOnHold = async (organizationId: string, projectId: string): Promise<void> => {
    const response = await this.projectStore.moveProjectToOnHold(organizationId, projectId);

    if (!response)
      toast.success('File moved to on hold successfully !!');
    else
      toast.error(response);
  }

  moveProjectFromOnHold = async (organizationId: string, projectId: string): Promise<void> => {
    const response = await this.projectStore.moveProjectFromOnHold(organizationId, projectId);

    if (!response)
      toast.success('File moved from on hold successfully !!');
    else
      toast.error(response);
  }
}
